import React, { useMemo, useCallback, memo } from 'react';
import { 
  Box, 
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Weekend as WeekendIcon,
  MeetingRoom as MeetingRoomIcon,
} from '@mui/icons-material';

// Tab Components
import MeetingRooms from './tabs/meetingRooms';
import WorkAreas from './tabs/workAreas';

// MOVE CONSTANTS OUTSIDE COMPONENT - they never change
const RESOURCE_TYPES = [
  { value: 'HD', label: 'Hot Desk' },
  { value: 'MR', label: 'Meeting Room' }
];

// MEMOIZED TabPanel component for navigation
const TabPanel = memo(function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`catalog-tabpanel-${index}`}
      aria-labelledby={`catalog-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
});

// MEMOIZED WorkAreas tab to prevent unnecessary re-renders
const MemoizedWorkAreas = memo(WorkAreas);

// MEMOIZED MeetingRooms to prevent unnecessary re-renders  
const MemoizedMeetingRooms = memo(MeetingRooms);

const CatalogueView = memo((props) => {
  const { 
    workAreas, resources, services, meetingRooms,
    activeTab, handleTabChange,
    handleAddWorkArea, handleUpdateWorkArea, handleDeleteWorkArea,
    handleAddResource, handleUpdateResource, handleDeleteResource,
    handleBulkActivateResources,
    getResourcesByType, venueInfo, isLoading
  } = props;
  
  // 🚀 OPTIMIZATION: Memoize helper functions with proper dependencies
  const getWorkAreaName = useCallback((workAreaId) => {
    const workArea = workAreas.find(wa => wa.id === workAreaId || wa.slug === workAreaId);
    return workArea ? workArea.name : 'Unknown Area';
  }, [workAreas]); // Only recreate when workAreas changes

  const getResourceTypeName = useCallback((typeCode) => {
    const type = RESOURCE_TYPES.find(t => t.value === typeCode);
    return type ? type.label : typeCode;
  }, []); // Never changes since RESOURCE_TYPES is constant

  const getTariffsForResource = useCallback((resourceId) => {
    // This would need to be implemented based on your tariff structure
    return [];
  }, []); // Implement proper dependencies when you add real logic

  // 🚀 OPTIMIZATION: Memoize filtered resources with proper dependencies
  const hotDeskResources = useMemo(() => {
    return getResourcesByType('HD');
  }, [getResourcesByType]); // Only depend on the function, not resources directly

  const meetingRoomResources = useMemo(() => {
    return getResourcesByType('MR');
  }, [getResourcesByType]); // Only depend on the function, not resources directly

  // 🚀 OPTIMIZATION: Memoize common props object with stable reference
  const commonTabProps = useMemo(() => ({
    workAreas,
    services,
    getWorkAreaName,
    getResourceTypeName,
    getTariffsForResource,
    resourceTypes: RESOURCE_TYPES, // This is always the same reference
    handleAddResource,
    handleUpdateResource,
    handleDeleteResource,
    handleAddWorkArea,
    handleUpdateWorkArea,
    handleDeleteWorkArea,
    handleBulkActivateResources,
    venueInfo,
  }), [
    workAreas,
    services,
    getWorkAreaName,
    getResourceTypeName,
    getTariffsForResource,
    handleAddResource,
    handleUpdateResource,
    handleDeleteResource,
    handleAddWorkArea,
    handleUpdateWorkArea,
    handleDeleteWorkArea,
    handleBulkActivateResources,
    venueInfo,
  ]);

  // 🚀 OPTIMIZATION: Memoize tab props to prevent child re-renders
  const workAreasTabProps = useMemo(() => ({
    resources: hotDeskResources,
    isLoading,
    resourceTypeFilter: 'HD', // Only show Hot Desks in this tab
    ...commonTabProps
  }), [hotDeskResources, isLoading, commonTabProps]);

  const meetingRoomsTabProps = useMemo(() => ({
    meetingRooms: meetingRooms,
    isLoading,
    resourceTypeFilter: 'MR', // Only show Meeting Rooms in this tab
    ...commonTabProps
  }), [meetingRooms, isLoading, commonTabProps]);

  return (
    <Box mt={2} id="venue-section-catalogue">
      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Hot Desks" icon={<WeekendIcon />} iconPosition="start" />
          <Tab label="Meeting Rooms" icon={<MeetingRoomIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <MemoizedWorkAreas {...workAreasTabProps} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <MemoizedMeetingRooms {...meetingRoomsTabProps} />
      </TabPanel>
    </Box>
  );
});

CatalogueView.displayName = 'CatalogueView';

export default CatalogueView;